import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  FileText, Clock, Users, Radio, PhoneIncoming,
  Filter, Download, RefreshCw, ChevronDown, ChevronUp,
  TrendingUp, Phone, BarChart3, Settings2, X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, LineChart, Line, ReferenceLine, Legend, Cell,
} from "recharts";
import { toast } from "sonner";
import { API_URL } from "../utils/api";

const API = API_URL;

const REPORT_TYPES = [
  { id: "billing_minutes", label: "Call Minutes", icon: Clock, description: "Minutes by call type for billing" },
  { id: "call_distribution", label: "Extension Load", icon: Users, description: "Call volume per extension" },
  { id: "channel_concurrency", label: "Channel Usage", icon: Radio, description: "Concurrent call analysis" },
  { id: "incoming_volume", label: "Incoming Calls", icon: PhoneIncoming, description: "Inbound call sources" },
];

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#f97316", "#ec4899"];

const TYPE_COLORS = {
  Inbound: "#3b82f6", Mobile: "#10b981", Local: "#f59e0b", National: "#8b5cf6",
  International: "#ef4444", Onnet: "#06b6d4", TollFree: "#f97316", Number: "#ec4899",
};

const DATE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7days", label: "Last 7 Days" },
  { value: "last30days", label: "Last 30 Days" },
  { value: "thisMonth", label: "This Month" },
  { value: "lastMonth", label: "Last Month" },
  { value: "last90days", label: "Last 90 Days" },
  { value: "all", label: "All Time" },
];

function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getDateRange(preset) {
  const today = new Date();
  const todayStr = formatDate(today);
  switch (preset) {
    case "today": return { from: todayStr, to: todayStr };
    case "yesterday": { const d = new Date(today); d.setDate(d.getDate() - 1); return { from: formatDate(d), to: formatDate(d) }; }
    case "last7days": { const d = new Date(today); d.setDate(d.getDate() - 7); return { from: formatDate(d), to: todayStr }; }
    case "last30days": { const d = new Date(today); d.setDate(d.getDate() - 30); return { from: formatDate(d), to: todayStr }; }
    case "thisMonth": { const d = new Date(today.getFullYear(), today.getMonth(), 1); return { from: formatDate(d), to: todayStr }; }
    case "lastMonth": { const s = new Date(today.getFullYear(), today.getMonth() - 1, 1); const e = new Date(today.getFullYear(), today.getMonth(), 0); return { from: formatDate(s), to: formatDate(e) }; }
    case "last90days": { const d = new Date(today); d.setDate(d.getDate() - 90); return { from: formatDate(d), to: todayStr }; }
    default: return { from: "", to: "" };
  }
}

function StatCard({ title, value, subtitle, icon: Icon, color = "primary" }) {
  const cls = {
    primary: "text-primary bg-primary/10",
    success: "text-emerald-500 bg-emerald-500/10",
    warning: "text-amber-500 bg-amber-500/10",
    error: "text-red-500 bg-red-500/10",
    info: "text-cyan-500 bg-cyan-500/10",
  };
  return (
    <Card className="bg-card border-border/50">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground truncate">{title}</p>
            <p className="text-2xl font-heading font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={`p-2.5 rounded-lg shrink-0 ${cls[color] || cls.primary}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== CHART COMPONENTS ====================

function BillingChart({ data, series }) {
  if (!data?.length) return <EmptyChart />;
  return (
    <ResponsiveContainer width="100%" height={350}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
        <XAxis dataKey="period" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
        <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} label={{ value: "Minutes", angle: -90, position: "insideLeft", style: { fill: "hsl(var(--muted-foreground))", fontSize: 12 } }} />
        <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", color: "hsl(var(--foreground))" }} />
        <Legend />
        {series?.map((s, i) => (
          <Bar key={s} dataKey={s} stackId="a" fill={TYPE_COLORS[s] || COLORS[i % COLORS.length]} radius={i === series.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function DistributionChart({ data }) {
  if (!data?.length) return <EmptyChart />;
  return (
    <ResponsiveContainer width="100%" height={Math.max(350, data.length * 32)}>
      <BarChart data={data} layout="vertical" margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
        <XAxis type="number" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
        <YAxis type="category" dataKey="extension" width={140} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
        <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", color: "hsl(var(--foreground))" }} />
        <Legend />
        <Bar dataKey="answered" stackId="a" fill="#10b981" name="Answered" radius={[0, 0, 0, 0]} />
        <Bar dataKey="missed" stackId="a" fill="#ef4444" name="Missed" radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function ConcurrencyChart({ data, channelLimit, onDayClick, selectedDate }) {
  if (!data?.length) return <EmptyChart />;
  const isHourly = data[0]?.hour !== undefined;
  return (
    <ResponsiveContainer width="100%" height={350}>
      <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
        onClick={(e) => {
          if (!isHourly && e?.activePayload?.[0]?.payload?.date && onDayClick) {
            onDayClick(e.activePayload[0].payload.date);
          }
        }}
        style={{ cursor: !isHourly && onDayClick ? "pointer" : "default" }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
        <XAxis dataKey={isHourly ? "hour" : "date"} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
        <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
        <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", color: "hsl(var(--foreground))" }} />
        <Area type="monotone" dataKey="peak" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} name="Peak Concurrent" />
        {channelLimit > 0 && (
          <ReferenceLine y={channelLimit} stroke="#ef4444" strokeDasharray="8 4" strokeWidth={2} label={{ value: `Limit: ${channelLimit}`, position: "right", fill: "#ef4444", fontSize: 12 }} />
        )}
      </AreaChart>
    </ResponsiveContainer>
  );
}

function IncomingChart({ data, timeChart, onCallerClick }) {
  if (!data?.length && !timeChart?.length) return <EmptyChart />;
  return (
    <div className="space-y-6">
      {timeChart?.length > 0 && (
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={timeChart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
            <XAxis dataKey="period" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
            <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
            <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", color: "hsl(var(--foreground))" }} />
            <Legend />
            <Area type="monotone" dataKey="answered" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.2} name="Answered" />
            <Area type="monotone" dataKey="missed" stackId="1" stroke="#ef4444" fill="#ef4444" fillOpacity={0.2} name="Missed" />
          </AreaChart>
        </ResponsiveContainer>
      )}
      {data?.length > 0 && (
        <ResponsiveContainer width="100%" height={Math.max(300, data.length * 28)}>
          <BarChart data={data} layout="vertical" margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
            onClick={(e) => {
              if (e?.activePayload?.[0]?.payload?.caller_number && onCallerClick) {
                onCallerClick(e.activePayload[0].payload.caller_number);
              }
            }}
            style={{ cursor: onCallerClick ? "pointer" : "default" }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
            <XAxis type="number" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
            <YAxis type="category" dataKey="caller" width={140} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
            <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", color: "hsl(var(--foreground))" }} />
            <Bar dataKey="calls" fill="#3b82f6" name="Calls" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
      {data?.length > 0 && onCallerClick && (
        <p className="text-xs text-muted-foreground text-center">Click a bar to filter by that caller</p>
      )}
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
      <div className="text-center">
        <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-30" />
        <p className="text-sm">No data for the selected filters</p>
      </div>
    </div>
  );
}

// ==================== DATA TABLE ====================

function DataTable({ columns, rows, page, pageSize, totalRows, onPageChange, onRowClick }) {
  const totalPages = Math.ceil(totalRows / pageSize);
  return (
    <div data-testid="report-data-table">
      <div className="overflow-x-auto rounded-lg border border-border/50">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-secondary/30 border-b border-border/50">
              {columns.map((col) => (
                <th key={col.key} className={`px-3 py-2.5 text-left font-medium text-muted-foreground whitespace-nowrap ${col.align === "right" ? "text-right" : ""}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={columns.length} className="px-3 py-8 text-center text-muted-foreground">No data</td></tr>
            ) : (
              rows.map((row, i) => (
                <tr key={i}
                  className={`border-b border-border/30 hover:bg-secondary/20 transition-colors ${onRowClick ? "cursor-pointer" : ""}`}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map((col) => (
                    <td key={col.key} className={`px-3 py-2 whitespace-nowrap ${col.align === "right" ? "text-right font-mono" : ""}`}>
                      {col.render ? col.render(row[col.key], row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalRows > pageSize && (
        <div className="flex items-center justify-between mt-3 px-1">
          <p className="text-xs text-muted-foreground">
            Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalRows)} of {totalRows}
          </p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== TABLE COLUMN DEFS ====================

const BILLING_COLUMNS = [
  { key: "date", label: "Date" },
  { key: "time", label: "Time" },
  { key: "direction", label: "Direction", render: (v) => <Badge variant="outline" className="text-xs capitalize">{v}</Badge> },
  { key: "call_type", label: "Type", render: (v) => <Badge variant="secondary" className="text-xs">{v}</Badge> },
  { key: "from", label: "From" },
  { key: "to", label: "To" },
  { key: "minutes", label: "Minutes", align: "right" },
  { key: "charge", label: "Charge", align: "right", render: (v) => v > 0 ? `$${v.toFixed(2)}` : "-" },
  { key: "status", label: "Status", render: (v) => <span className={v === "Ok" ? "text-emerald-400" : "text-amber-400"}>{v}</span> },
];

const DISTRIBUTION_COLUMNS = [
  { key: "extension", label: "Extension" },
  { key: "extension_number", label: "Number", render: (v) => <span className="font-mono text-xs text-muted-foreground">{v}</span> },
  { key: "total_calls", label: "Total Calls", align: "right" },
  { key: "answered", label: "Answered", align: "right", render: (v) => <span className="text-emerald-400">{v}</span> },
  { key: "missed", label: "Missed", align: "right", render: (v) => <span className={v > 0 ? "text-red-400" : ""}>{v}</span> },
  { key: "missed_pct", label: "Missed %", align: "right", render: (v) => `${v}%` },
  { key: "inbound", label: "Inbound", align: "right" },
  { key: "outbound", label: "Outbound", align: "right" },
  { key: "total_minutes", label: "Minutes", align: "right" },
  { key: "avg_handle_minutes", label: "Avg Handle", align: "right", render: (v) => `${v}m` },
];

const CONCURRENCY_COLUMNS_DAILY = [
  { key: "date", label: "Date" },
  { key: "peak", label: "Peak Concurrent", align: "right", render: (v, row) => {
    const limit = row.channel_limit;
    const atLimit = limit > 0 && v >= limit;
    return <span className={atLimit ? "text-red-400 font-bold" : ""}>{v}{atLimit ? " (AT LIMIT)" : ""}</span>;
  }},
  { key: "channel_limit", label: "Channel Limit", align: "right", render: (v) => v > 0 ? v : "-" },
];

const CONCURRENCY_COLUMNS_DIST = [
  { key: "concurrent_calls", label: "Concurrent Calls", align: "right" },
  { key: "occurrences", label: "Occurrences", align: "right" },
  { key: "pct", label: "% of Total", align: "right", render: (v) => `${v}%` },
];

const PEAK_DETAIL_COLUMNS = [
  { key: "date", label: "Date" },
  { key: "time", label: "Time" },
  { key: "from", label: "From" },
  { key: "to", label: "To / Answered By" },
  { key: "minutes", label: "Minutes", align: "right" },
  { key: "call_type", label: "Type", render: (v) => <Badge variant="secondary" className="text-xs">{v}</Badge> },
  { key: "status", label: "Status", render: (v) => <span className={v === "Ok" ? "text-emerald-400" : "text-amber-400"}>{v}</span> },
];

const INCOMING_COLUMNS = [
  { key: "caller", label: "Caller" },
  { key: "caller_number", label: "Number", render: (v) => <span className="font-mono text-xs text-muted-foreground">{v}</span> },
  { key: "total_calls", label: "Calls", align: "right" },
  { key: "answered", label: "Answered", align: "right", render: (v) => <span className="text-emerald-400">{v}</span> },
  { key: "missed", label: "Missed", align: "right", render: (v) => <span className={v > 0 ? "text-red-400" : ""}>{v}</span> },
  { key: "missed_pct", label: "Missed %", align: "right", render: (v) => `${v}%` },
  { key: "total_minutes", label: "Minutes", align: "right" },
];

const INCOMING_DETAIL_COLUMNS = [
  { key: "date", label: "Date" },
  { key: "time", label: "Time" },
  { key: "to", label: "Answered By" },
  { key: "minutes", label: "Minutes", align: "right" },
  { key: "call_type", label: "Type", render: (v) => <Badge variant="secondary" className="text-xs">{v}</Badge> },
  { key: "status", label: "Status", render: (v) => <span className={v === "Ok" ? "text-emerald-400" : "text-amber-400"}>{v}</span> },
];

// ==================== MAIN COMPONENT ====================

export default function Reports() {
  const { token } = useAuth();
  const [activeReport, setActiveReport] = useState("billing_minutes");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [filterOptions, setFilterOptions] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const defaultRange = getDateRange("last30days");
  const [filters, setFilters] = useState({
    datePreset: "last30days",
    date_from: defaultRange.from,
    date_to: defaultRange.to,
    call_types: [],
    directions: [],
    extensions: [],
    group_by: "day",
    caller: "",
  });

  // Channel limit state
  const [channelLimit, setChannelLimit] = useState(0);
  const [editingLimit, setEditingLimit] = useState(false);
  const [limitInput, setLimitInput] = useState("");

  // Debounced caller input
  const [callerInput, setCallerInput] = useState("");
  const callerDebounceRef = useRef(null);

  // Channel peak drill-down state
  const [peakDetail, setPeakDetail] = useState(null);
  const [peakDetailLoading, setPeakDetailLoading] = useState(false);

  useEffect(() => {
    fetchFilterOptions();
    fetchChannelLimit();
  }, []);

  useEffect(() => {
    setPage(1);
    setPeakDetail(null);
    fetchReport();
  }, [activeReport, filters]);

  const fetchFilterOptions = async () => {
    try {
      const res = await axios.get(`${API}/reports/filter-options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFilterOptions(res.data);
    } catch (e) {
      console.error("Failed to fetch filter options:", e);
    }
  };

  const fetchChannelLimit = async () => {
    try {
      const res = await axios.get(`${API}/reports/channel-limit`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChannelLimit(res.data.channel_limit || 0);
    } catch (e) {
      console.error(e);
    }
  };

  const saveChannelLimit = async () => {
    try {
      const val = parseInt(limitInput) || 0;
      await axios.put(`${API}/reports/channel-limit`, { channel_limit: val }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChannelLimit(val);
      setEditingLimit(false);
      toast.success("Channel limit updated");
      if (activeReport === "channel_concurrency") fetchReport();
    } catch (e) {
      toast.error("Failed to save channel limit");
    }
  };

  const fetchReport = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const body = {
        report_type: activeReport,
        filters: {
          date_from: filters.date_from || undefined,
          date_to: filters.date_to || undefined,
          call_types: filters.call_types.length ? filters.call_types : undefined,
          directions: filters.directions.length ? filters.directions : undefined,
          extensions: filters.extensions.length ? filters.extensions : undefined,
          caller: filters.caller || undefined,
          group_by: filters.group_by,
        },
        page: p,
        page_size: pageSize,
      };
      const res = await axios.post(`${API}/reports/generate`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReport(res.data);
    } catch (e) {
      console.error("Report error:", e);
      toast.error("Failed to generate report");
    } finally {
      setLoading(false);
    }
  }, [activeReport, filters, token]);

  const handlePageChange = (p) => {
    setPage(p);
    fetchReport(p);
  };

  const fetchPeakDetails = async (date) => {
    setPeakDetailLoading(true);
    try {
      const res = await axios.post(`${API}/reports/channel-peak-details`, { date }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPeakDetail(res.data);
    } catch (e) {
      console.error("Peak detail error:", e);
      toast.error("Failed to fetch peak details");
    } finally {
      setPeakDetailLoading(false);
    }
  };

  const handlePresetChange = (preset) => {
    const range = getDateRange(preset);
    setFilters((f) => ({ ...f, datePreset: preset, date_from: range.from, date_to: range.to }));
  };

  const toggleFilter = (key, value) => {
    setFilters((f) => {
      const arr = f[key] || [];
      return { ...f, [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });
  };

  // Summary cards per report type
  const renderSummary = () => {
    if (!report?.summary) return null;
    const s = report.summary;

    switch (activeReport) {
      case "billing_minutes":
        return (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="billing-summary">
            <StatCard title="Total Calls" value={s.total_calls?.toLocaleString()} icon={Phone} />
            <StatCard title="Total Minutes" value={s.total_minutes?.toLocaleString()} icon={Clock} color="info" />
            <StatCard title="Mobile Minutes" value={s.mobile_minutes?.toLocaleString()} icon={Phone} color="warning" />
            <StatCard title="Local & National Minutes" value={s.local_national_minutes?.toLocaleString()} icon={TrendingUp} color="success" />
          </div>
        );
      case "call_distribution":
        return (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="distribution-summary">
            <StatCard title="Total Calls" value={s.total_calls?.toLocaleString()} icon={Phone} />
            <StatCard title="Active Extensions" value={s.extensions_active} icon={Users} color="info" />
            <StatCard title="Avg Calls/Extension" value={s.avg_calls_per_ext} icon={TrendingUp} color="success" />
            <StatCard title="Busiest Extension" value={s.busiest_extension} subtitle={`${s.busiest_extension_calls} calls`} icon={BarChart3} color="warning" />
          </div>
        );
      case "channel_concurrency":
        return (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="concurrency-summary">
            <StatCard title="Total External Calls" value={s.total_external_calls?.toLocaleString()} icon={Phone} />
            <StatCard title="Peak Concurrent" value={s.peak_concurrent} subtitle={channelLimit ? `Limit: ${channelLimit}` : "No limit set"} icon={Radio} color={s.peak_concurrent >= channelLimit && channelLimit > 0 ? "error" : "info"} />
            <StatCard title="Avg Daily Peak" value={s.avg_daily_peak} icon={TrendingUp} color="success" />
            <StatCard title="Days at Capacity" value={s.days_at_capacity} icon={BarChart3} color={s.days_at_capacity > 0 ? "warning" : "success"} />
          </div>
        );
      case "incoming_volume":
        return (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="incoming-summary">
            <StatCard title="Total Inbound" value={s.total_inbound_calls?.toLocaleString()} icon={PhoneIncoming} />
            <StatCard title="Unique Callers" value={s.unique_callers?.toLocaleString()} icon={Users} color="info" />
            <StatCard title="Answer Rate" value={`${s.answer_rate}%`} icon={TrendingUp} color="success" />
            <StatCard title="Top Caller" value={s.top_caller} subtitle={`${s.top_caller_calls} calls`} icon={Phone} color="warning" />
          </div>
        );
      default:
        return null;
    }
  };

  const renderChart = () => {
    if (!report) return null;
    switch (activeReport) {
      case "billing_minutes": return <BillingChart data={report.chart_data} series={report.chart_series} />;
      case "call_distribution": return <DistributionChart data={report.chart_data} />;
      case "channel_concurrency": return <ConcurrencyChart data={report.chart_data} channelLimit={channelLimit} onDayClick={(date) => fetchPeakDetails(date)} selectedDate={peakDetail?.date} />;
      case "incoming_volume": return <IncomingChart data={report.chart_data} timeChart={report.time_chart} onCallerClick={(num) => { setCallerInput(num); setFilters((f) => ({ ...f, caller: num })); }} />;
      default: return null;
    }
  };

  const getTableColumns = () => {
    switch (activeReport) {
      case "billing_minutes": return BILLING_COLUMNS;
      case "call_distribution": return DISTRIBUTION_COLUMNS;
      case "channel_concurrency": return filters.group_by === "hour" ? CONCURRENCY_COLUMNS_DIST : CONCURRENCY_COLUMNS_DAILY;
      case "incoming_volume": return report?.call_details ? INCOMING_DETAIL_COLUMNS : INCOMING_COLUMNS;
      default: return [];
    }
  };

  const getTableData = () => {
    if (activeReport === "incoming_volume" && report?.call_details) {
      return {
        rows: report.call_details,
        total: report.call_details_total,
      };
    }
    return {
      rows: report?.table_data || [],
      total: report?.total_rows || 0,
    };
  };

  // Type breakdown badges for billing
  const renderTypeBreakdown = () => {
    if (activeReport !== "billing_minutes" || !report?.summary?.by_type) return null;
    return (
      <div className="flex flex-wrap gap-2 mt-3">
        {Object.entries(report.summary.by_type).map(([type, data]) => (
          <div key={type} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary/40 border border-border/30">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TYPE_COLORS[type] || "#888" }} />
            <span className="text-xs font-medium">{type}</span>
            <span className="text-xs text-muted-foreground">{data.minutes}m</span>
            <span className="text-xs text-muted-foreground">({data.calls})</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground text-sm">Generate and analyze call data reports</p>
        </div>
        <div className="flex items-center gap-2">
          {activeReport === "channel_concurrency" && (
            editingLimit ? (
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  value={limitInput}
                  onChange={(e) => setLimitInput(e.target.value)}
                  className="w-20 h-8 text-sm"
                  placeholder="Limit"
                  data-testid="channel-limit-input"
                />
                <Button size="sm" variant="default" onClick={saveChannelLimit} data-testid="save-channel-limit">Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingLimit(false)}>Cancel</Button>
              </div>
            ) : (
              <Button
                variant="outline" size="sm"
                onClick={() => { setLimitInput(String(channelLimit)); setEditingLimit(true); }}
                className="gap-1.5" data-testid="edit-channel-limit"
              >
                <Settings2 className="w-3.5 h-3.5" />
                Channel Limit: {channelLimit || "Not set"}
              </Button>
            )
          )}
          <Button variant="outline" size="sm" onClick={() => fetchReport(page)} disabled={loading} className="gap-1.5" data-testid="refresh-report">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Report Type Tabs */}
      <Tabs value={activeReport} onValueChange={(v) => { setActiveReport(v); setPage(1); }}>
        <TabsList className="grid w-full grid-cols-4 lg:w-[600px]" data-testid="report-tabs">
          {REPORT_TYPES.map((rt) => (
            <TabsTrigger key={rt.id} value={rt.id} className="gap-1.5 text-xs sm:text-sm" data-testid={`tab-${rt.id}`}>
              <rt.icon className="w-3.5 h-3.5 hidden sm:block" />
              {rt.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Filters */}
      <Card className="bg-card border-border/50">
        <CardContent className="p-4">
          <button onClick={() => setFiltersOpen(!filtersOpen)} className="flex items-center gap-2 w-full text-left mb-2" data-testid="toggle-filters">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filters</span>
            {filtersOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground ml-auto" /> : <ChevronDown className="w-4 h-4 text-muted-foreground ml-auto" />}
          </button>
          {filtersOpen && (
            <div className="space-y-3 pt-2 border-t border-border/30">
              {/* Date range */}
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Period</Label>
                  <Select value={filters.datePreset} onValueChange={handlePresetChange}>
                    <SelectTrigger className="w-[150px] h-8 text-sm" data-testid="date-preset-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DATE_PRESETS.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">From</Label>
                  <Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value, datePreset: "custom" }))} className="h-8 text-sm w-[140px]" data-testid="date-from" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">To</Label>
                  <Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value, datePreset: "custom" }))} className="h-8 text-sm w-[140px]" data-testid="date-to" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Group By</Label>
                  <Select value={filters.group_by} onValueChange={(v) => setFilters((f) => ({ ...f, group_by: v }))}>
                    <SelectTrigger className="w-[110px] h-8 text-sm" data-testid="group-by-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="day">Day</SelectItem>
                      <SelectItem value="week">Week</SelectItem>
                      <SelectItem value="month">Month</SelectItem>
                      <SelectItem value="hour">Hour</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Call type + direction chips (hidden for incoming_volume since all are inbound) */}
              {activeReport !== "incoming_volume" && (
                <div className="flex flex-wrap gap-4">
                  {filterOptions?.call_types?.length > 0 && (
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">Call Type</Label>
                      <div className="flex flex-wrap gap-1">
                        {filterOptions.call_types.map((ct) => (
                          <button
                            key={ct}
                            onClick={() => toggleFilter("call_types", ct)}
                            className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${
                              filters.call_types.includes(ct)
                                ? "bg-primary/20 border-primary/50 text-primary"
                                : "bg-secondary/30 border-border/50 text-muted-foreground hover:bg-secondary/50"
                            }`}
                            data-testid={`filter-type-${ct}`}
                          >
                            {ct}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {filterOptions?.directions?.length > 0 && (
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">Direction</Label>
                      <div className="flex flex-wrap gap-1">
                        {filterOptions.directions.map((d) => (
                          <button
                            key={d}
                            onClick={() => toggleFilter("directions", d)}
                            className={`px-2 py-0.5 rounded-full text-xs border capitalize transition-colors ${
                              filters.directions.includes(d)
                                ? "bg-primary/20 border-primary/50 text-primary"
                                : "bg-secondary/30 border-border/50 text-muted-foreground hover:bg-secondary/50"
                            }`}
                            data-testid={`filter-dir-${d}`}
                          >
                            {d}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Extension filter for distribution/concurrency */}
              {(activeReport === "call_distribution" || activeReport === "channel_concurrency") && filterOptions?.extensions?.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Extensions</Label>
                  <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                    {filterOptions.extensions.map((ext) => (
                      <button
                        key={ext.number}
                        onClick={() => toggleFilter("extensions", ext.number)}
                        className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${
                          filters.extensions.includes(ext.number)
                            ? "bg-primary/20 border-primary/50 text-primary"
                            : "bg-secondary/30 border-border/50 text-muted-foreground hover:bg-secondary/50"
                        }`}
                        data-testid={`filter-ext-${ext.number}`}
                      >
                        {ext.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Caller ID filter for incoming volume */}
              {activeReport === "incoming_volume" && (
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Caller ID</Label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1 max-w-xs">
                      <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <Input
                        type="text"
                        value={callerInput}
                        onChange={(e) => {
                          const val = e.target.value;
                          setCallerInput(val);
                          clearTimeout(callerDebounceRef.current);
                          callerDebounceRef.current = setTimeout(() => {
                            setFilters((f) => ({ ...f, caller: val }));
                          }, 600);
                        }}
                        placeholder="Search by number or name..."
                        className="h-8 text-sm pl-8"
                        data-testid="caller-filter-input"
                      />
                    </div>
                    {callerInput && (
                      <Button variant="ghost" size="sm" className="h-8 px-2 text-xs"
                        onClick={() => { setCallerInput(""); setFilters((f) => ({ ...f, caller: "" })); }}
                        data-testid="clear-caller-filter"
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Filter by phone number to filter.</p>
                </div>
              )}

              {/* Active filter summary */}
              {(filters.call_types.length > 0 || filters.directions.length > 0 || filters.extensions.length > 0 || filters.caller) && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Active filters:</span>
                  {filters.caller && (
                    <Badge variant="secondary" className="text-xs gap-1">
                      Caller: {filters.caller}
                      <button onClick={() => { setCallerInput(""); setFilters((f) => ({ ...f, caller: "" })); }} className="ml-0.5 hover:text-foreground">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                  <Button variant="ghost" size="sm" className="text-xs h-6"
                    onClick={() => { setCallerInput(""); setFilters((f) => ({ ...f, call_types: [], directions: [], extensions: [], caller: "" })); }}
                    data-testid="clear-filters"
                  >
                    Clear all
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loading overlay */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Generating report...</span>
        </div>
      )}

      {/* Results */}
      {!loading && report && (
        <div className="space-y-5">
          {/* Summary Cards */}
          {renderSummary()}
          {renderTypeBreakdown()}

          {/* Chart */}
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-heading">
                {REPORT_TYPES.find((r) => r.id === activeReport)?.label}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {renderChart()}
            </CardContent>
          </Card>

          {/* Data Table */}
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-heading">
                  {report?.call_details ? "Call Details" : "Detail Data"}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {activeReport === "channel_concurrency" && filters.group_by !== "hour" && (
                    <p className="text-xs text-muted-foreground">Click a row to see peak calls</p>
                  )}
                  <Badge variant="secondary" className="text-xs">{getTableData().total} rows</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <DataTable
                columns={getTableColumns()}
                rows={getTableData().rows}
                page={page}
                pageSize={pageSize}
                totalRows={getTableData().total}
                onPageChange={handlePageChange}
                onRowClick={activeReport === "channel_concurrency" && filters.group_by !== "hour" ? (row) => fetchPeakDetails(row.date) : undefined}
              />
            </CardContent>
          </Card>

          {/* Channel Peak Detail Drill-down */}
          {activeReport === "channel_concurrency" && (peakDetail || peakDetailLoading) && (
            <Card className="bg-card border-border/50 border-primary/30" data-testid="peak-detail-panel">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-heading">
                      Peak Calls — {peakDetail?.date}
                    </CardTitle>
                    {peakDetail && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {peakDetail.peak} concurrent calls at {peakDetail.peak_time}
                      </p>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setPeakDetail(null)} data-testid="close-peak-detail">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {peakDetailLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <RefreshCw className="w-5 h-5 animate-spin text-primary" />
                    <span className="ml-2 text-muted-foreground text-sm">Loading peak calls...</span>
                  </div>
                ) : peakDetail?.calls?.length > 0 ? (
                  <DataTable
                    columns={PEAK_DETAIL_COLUMNS}
                    rows={peakDetail.calls}
                    page={1}
                    pageSize={100}
                    totalRows={peakDetail.calls.length}
                    onPageChange={() => {}}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground py-4 text-center">No call data found for this peak.</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
